import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { BarChart3 } from "lucide-react";
import { googleAdsApi, type GoogleCampaign } from "../api/googleAds.ts";
import { cn } from "../lib/cn.ts";

const DATE_RANGES = [
  { value: "TODAY", label: "Today" },
  { value: "YESTERDAY", label: "Yesterday" },
  { value: "LAST_7_DAYS", label: "Last 7 days" },
  { value: "LAST_30_DAYS", label: "Last 30 days" },
  { value: "THIS_MONTH", label: "This month" },
  { value: "LAST_MONTH", label: "Last month" },
];

const statusColors: Record<string, string> = {
  ENABLED: "bg-green-100 text-green-700",
  PAUSED: "bg-yellow-100 text-yellow-700",
  REMOVED: "bg-red-100 text-red-700",
};

function fmt(n: number, decimals = 0) {
  return n.toLocaleString("en-US", { maximumFractionDigits: decimals });
}

function BudgetModal({
  campaign,
  onClose,
  onSave,
  saving,
}: {
  campaign: GoogleCampaign;
  onClose: () => void;
  onSave: (budget: number) => void;
  saving: boolean;
}) {
  const [value, setValue] = useState(
    campaign.daily_budget != null ? String(campaign.daily_budget) : ""
  );

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-xl p-6 w-full max-w-sm">
        <h3 className="text-base font-semibold text-slate-900 mb-1">Edit Daily Budget</h3>
        <p className="text-sm text-slate-500 mb-4 truncate">{campaign.name}</p>
        <div className="flex items-center gap-2 mb-6">
          <span className="text-slate-500 text-sm">$</span>
          <input
            type="number"
            step="0.01"
            min="0.01"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            className="input-base flex-1"
            autoFocus
          />
          <span className="text-slate-500 text-sm">/ day</span>
        </div>
        <div className="flex gap-2 justify-end">
          <button onClick={onClose} className="btn-secondary">Cancel</button>
          <button
            onClick={() => { const v = parseFloat(value); if (!isNaN(v) && v > 0) onSave(v); }}
            disabled={saving || !value || isNaN(parseFloat(value))}
            className="btn-primary disabled:opacity-50"
          >
            {saving ? "Saving…" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default function GoogleAdsPage() {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const [selectedCustomerId, setSelectedCustomerId] = useState("");
  const [dateRange, setDateRange] = useState("LAST_30_DAYS");
  const [budgetCampaign, setBudgetCampaign] = useState<GoogleCampaign | null>(null);

  const { data: accounts = [], isLoading: accountsLoading, error: accountsError } = useQuery({
    queryKey: ["google-ads-accounts"],
    queryFn: googleAdsApi.listAccounts,
  });

  const { data: campaigns = [], isLoading: campaignsLoading } = useQuery({
    queryKey: ["google-ads-campaigns", selectedCustomerId, dateRange],
    queryFn: () => googleAdsApi.listCampaigns(selectedCustomerId, dateRange),
    enabled: !!selectedCustomerId,
  });

  const pauseMutation = useMutation({
    mutationFn: ({ campaignId }: { campaignId: string }) =>
      googleAdsApi.pauseCampaign(selectedCustomerId, campaignId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["google-ads-campaigns"] }),
  });

  const enableMutation = useMutation({
    mutationFn: ({ campaignId }: { campaignId: string }) =>
      googleAdsApi.enableCampaign(selectedCustomerId, campaignId),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ["google-ads-campaigns"] }),
  });

  const budgetMutation = useMutation({
    mutationFn: ({ campaignId, budget }: { campaignId: string; budget: number }) =>
      googleAdsApi.updateBudget(selectedCustomerId, campaignId, budget),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["google-ads-campaigns"] });
      setBudgetCampaign(null);
    },
  });

  const totalImpressions = campaigns.reduce((s, c) => s + c.impressions, 0);
  const totalClicks = campaigns.reduce((s, c) => s + c.clicks, 0);
  const totalCost = campaigns.reduce((s, c) => s + c.cost, 0);
  const totalConversions = campaigns.reduce((s, c) => s + c.conversions, 0);
  const activeCampaigns = campaigns.filter((c) => c.status === "ENABLED").length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl font-bold text-slate-900">{t("nav.googleAds")}</h1>
          <p className="mt-0.5 text-sm text-slate-500">
            {selectedCustomerId
              ? `${campaigns.length} campaigns · ${activeCampaigns} active`
              : "Select an account to view campaigns"}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row gap-2">
          <select
            value={selectedCustomerId}
            onChange={(e) => setSelectedCustomerId(e.target.value)}
            className="input-base sm:w-64"
            disabled={accountsLoading}
          >
            <option value="">
              {accountsLoading ? "Loading accounts…" : "Select account…"}
            </option>
            {accounts.map((a) => (
              <option key={a.customer_id} value={a.customer_id}>
                {a.name ? `${a.name} (${a.customer_id})` : a.customer_id}
              </option>
            ))}
          </select>
          {selectedCustomerId && (
            <select
              value={dateRange}
              onChange={(e) => setDateRange(e.target.value)}
              className="input-base sm:w-40"
            >
              {DATE_RANGES.map((r) => (
                <option key={r.value} value={r.value}>{r.label}</option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Error */}
      {accountsError && (
        <div className="rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-700">
          Failed to load Google Ads accounts. Check that your credentials and developer token are configured correctly.
        </div>
      )}

      {/* Summary cards */}
      {selectedCustomerId && campaigns.length > 0 && (
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          {[
            { label: "Impressions", value: fmt(totalImpressions) },
            { label: "Clicks", value: fmt(totalClicks) },
            { label: "Spend", value: `$${fmt(totalCost, 2)}` },
            { label: "Conversions", value: fmt(totalConversions, 1) },
          ].map((card) => (
            <div key={card.label} className="rounded-xl border border-slate-200 bg-white p-4">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">{card.label}</p>
              <p className="mt-1 text-2xl font-bold text-slate-900">{card.value}</p>
            </div>
          ))}
        </div>
      )}

      {/* Table */}
      {!selectedCustomerId ? (
        <div className="rounded-xl border border-slate-200 bg-white p-12 text-center">
          <BarChart3 className="mx-auto h-10 w-10 text-slate-300 mb-3" />
          <p className="font-medium text-slate-700">Select a Google Ads account</p>
          <p className="mt-1 text-sm text-slate-400">Choose an account from the dropdown above to view campaigns.</p>
        </div>
      ) : campaignsLoading ? (
        <p className="text-sm text-slate-500">Loading campaigns…</p>
      ) : campaigns.length === 0 ? (
        <div className="rounded-xl border border-slate-200 bg-white p-12 text-center">
          <p className="font-medium text-slate-700">No campaigns found</p>
          <p className="mt-1 text-sm text-slate-400">No campaigns in this account for the selected date range.</p>
        </div>
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <table className="w-full text-sm">
            <thead className="border-b border-slate-200 bg-slate-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Campaign</th>
                <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-slate-500">Status</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Budget/day</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Impr.</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Clicks</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">CTR</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Cost</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Conv.</th>
                <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-slate-500">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {campaigns.map((campaign) => {
                const isToggling =
                  (pauseMutation.isPending && pauseMutation.variables?.campaignId === campaign.id) ||
                  (enableMutation.isPending && enableMutation.variables?.campaignId === campaign.id);

                return (
                  <tr key={campaign.id} className="hover:bg-slate-50/60">
                    <td className="px-4 py-3">
                      <p className="font-medium text-slate-900">{campaign.name}</p>
                      <p className="text-xs text-slate-400 mt-0.5">{campaign.channel_type} · {campaign.bidding_strategy}</p>
                    </td>
                    <td className="px-4 py-3">
                      <span className={cn("inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium", statusColors[campaign.status] ?? "bg-slate-100 text-slate-600")}>
                        {campaign.status}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-right text-slate-700">
                      {campaign.daily_budget != null ? (
                        <button onClick={() => setBudgetCampaign(campaign)} className="hover:text-brand-600 hover:underline">
                          ${fmt(campaign.daily_budget, 2)}
                        </button>
                      ) : (
                        <button onClick={() => setBudgetCampaign(campaign)} className="text-xs text-slate-400 hover:text-brand-600">Set</button>
                      )}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-700">{fmt(campaign.impressions)}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{fmt(campaign.clicks)}</td>
                    <td className="px-4 py-3 text-right text-slate-700">
                      {campaign.ctr > 0 ? `${(campaign.ctr * 100).toFixed(2)}%` : "—"}
                    </td>
                    <td className="px-4 py-3 text-right text-slate-700">${fmt(campaign.cost, 2)}</td>
                    <td className="px-4 py-3 text-right text-slate-700">{fmt(campaign.conversions, 1)}</td>
                    <td className="px-4 py-3 text-right">
                      <div className="flex items-center justify-end gap-1.5">
                        {campaign.status === "ENABLED" ? (
                          <button
                            onClick={() => pauseMutation.mutate({ campaignId: campaign.id })}
                            disabled={isToggling}
                            className="rounded-md border border-yellow-300 px-2.5 py-1 text-xs font-medium text-yellow-700 hover:bg-yellow-50 disabled:opacity-50"
                          >
                            {isToggling ? "…" : "Pause"}
                          </button>
                        ) : campaign.status === "PAUSED" ? (
                          <button
                            onClick={() => enableMutation.mutate({ campaignId: campaign.id })}
                            disabled={isToggling}
                            className="rounded-md border border-green-300 px-2.5 py-1 text-xs font-medium text-green-700 hover:bg-green-50 disabled:opacity-50"
                          >
                            {isToggling ? "…" : "Enable"}
                          </button>
                        ) : null}
                        <button
                          onClick={() => setBudgetCampaign(campaign)}
                          className="rounded-md border border-slate-200 px-2.5 py-1 text-xs font-medium text-slate-600 hover:bg-slate-50"
                        >
                          Budget
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}

      {budgetCampaign && (
        <BudgetModal
          campaign={budgetCampaign}
          onClose={() => setBudgetCampaign(null)}
          onSave={(budget) => budgetMutation.mutate({ campaignId: budgetCampaign.id, budget })}
          saving={budgetMutation.isPending}
        />
      )}
    </div>
  );
}
